/**
 * Bundle Notes → Order Note
 *
 * Copies `custom.bundle_notes` product metafield text into the cart note
 * so it appears on packing slips as `order.note`.
 *
 * Data is read from a hidden <span class="js-bundle-notes"> rendered by
 * buy-buttons.liquid inside each bundle product's form.
 *
 * Cart attributes prefixed `_bn_<productId>` track which bundles are in
 * the cart so notes can be cleaned up when items are removed.
 */
(function BundleNotesController() {
  'use strict';

  var ATTR_PREFIX = '_bn_';
  var SECTION_HEADER = '--- Packing notes ---';
  var isUpdating = false;

  function init() {
    reconcile();

    // Listen at document level so dynamically-injected quick buy forms are
    // covered - their forms don't exist in the DOM at DOMContentLoaded, so
    // per-form attachment at init time would miss them.
    document.addEventListener('variant:add', onVariantAdd);
    document.documentElement.addEventListener('cart:change', onCartChange);

    // Capture phase so we fire before CartNote's bubble-phase listener.
    // This lets us send the full note (user text + bundle section) and call
    // stopImmediatePropagation() to prevent CartNote from overwriting it with
    // just the raw textarea value.
    document.addEventListener('change', onNoteChange, true);
  }

  function onVariantAdd(event) {
    var form = event.target;
    if (!form) return;
    var notesEl = form.querySelector('.js-bundle-notes');
    if (!notesEl) return;
    var productId = notesEl.dataset.productId;
    var notes = notesEl.dataset.notes;
    if (productId && notes) setBundle(productId, notes);
  }

  function onNoteChange(event) {
    if (event.target.getAttribute('name') !== 'note') return;
    event.stopImmediatePropagation();

    var newNoteText = event.target.value;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var note = buildNote(newNoteText, cart.attributes);
        return updateCart(note, cart.attributes);
      })
      .catch(function (e) { console.error('[bundle-notes] onNoteChange error', e); });
  }

  function onCartChange(event) {
    if (isUpdating) return;
    if (event.detail && event.detail.baseEvent === 'variant:add') return;
    reconcile();
  }

  function setBundle(productId, notes) {
    if (isUpdating) return;
    isUpdating = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = Object.assign({}, cart.attributes);
        attrs[ATTR_PREFIX + productId] = notes;

        var note = buildNote(cart.note, attrs);
        if (note === cart.note && attrs[ATTR_PREFIX + productId] === cart.attributes[ATTR_PREFIX + productId]) {
          return null;
        }
        return updateCart(note, attrs);
      })
      .catch(function (e) { console.error('[bundle-notes] setBundle error', e); })
      .then(function () { isUpdating = false; });
  }

  function reconcile() {
    if (isUpdating) return;
    isUpdating = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var productIds = getTrackedProductIds(cart.items);
        var attrs = Object.assign({}, cart.attributes);
        var changed = false;

        Object.keys(attrs).forEach(function (key) {
          if (key.indexOf(ATTR_PREFIX) !== 0) return;
          if (productIds.indexOf(key.slice(ATTR_PREFIX.length)) === -1) {
            attrs[key] = '';
            changed = true;
          }
        });

        var note = buildNote(cart.note, attrs);
        if (note === cart.note && !changed) return null;
        return updateCart(note, attrs);
      })
      .catch(function (e) { console.error('[bundle-notes] reconcile error', e); })
      .then(function () { isUpdating = false; });
  }

  function getTrackedProductIds(items) {
    var productIds = [];

    walkItems(items, function (item) {
      if (item && item.product_id != null) {
        productIds.push(String(item.product_id));
      }
    });

    return productIds;
  }

  function walkItems(items, visit, seen) {
    if (!Array.isArray(items) || !items.length) return;

    seen = seen || [];

    items.forEach(function (item) {
      if (!item || seen.indexOf(item) !== -1) return;

      seen.push(item);
      visit(item);

      if (Array.isArray(item.item_components) && item.item_components.length) {
        walkItems(item.item_components, visit, seen);
      }

      if (item.parent_relationship && item.parent_relationship.parent) {
        walkItems([item.parent_relationship.parent], visit, seen);
      }
    });
  }

  /**
   * Builds the full note string: customer text (preserved) + bundle section.
   * Splits on SECTION_HEADER to find the boundary — works whether or not
   * there is existing customer text before the header.
   */
  function buildNote(currentNote, attrs) {
    var customerText = (currentNote || '').split(SECTION_HEADER)[0].trim();
    var bundleLines = Object.keys(attrs)
      .filter(function (k) { return k.indexOf(ATTR_PREFIX) === 0 && attrs[k]; })
      .map(function (k) { return attrs[k]; });

    if (!bundleLines.length) return customerText;
    return (customerText ? customerText + '\n\n' : '')
      + SECTION_HEADER + '\n'
      + bundleLines.join('\n');
  }

  function updateCart(note, attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note, attributes: attributes })
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
